class ArmoniaCartUpsell extends HTMLElement {
  constructor() {
    super();

    this.abortController = null;
    this.products = [];

    this.labels = {
      shippingHeading: 'Još malo do besplatne dostave',
      defaultHeading: 'Možda vam se dopadne i ovo',
      add: 'Dodaj',
      adding: 'Dodajem...',
      choose: 'Izaberi',
      from: 'Od',
      addError: 'Trenutno nije moguće dodati ovaj proizvod u korpu. Pokušajte ponovo.',
    };
  }

  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    if (this.dataset.enabled !== 'true') {
      this.hidden = true;
      return;
    }

    this.limit = this.clampNumber(Number(this.dataset.limit || 2), 1, 4);
    this.cartTotal = Number(this.dataset.cartTotal || 0);
    this.freeShippingThreshold = Number(this.dataset.freeShippingThreshold || 0);
    this.relatedEnabled = this.dataset.relatedEnabled !== 'false';

    this.cartItems = this.readJSON('[data-armonia-cart-items]', []);
    this.fallbackProducts = this.readJSON('[data-armonia-fallback-products]', []);

    this.cartProductIds = [...new Set(
      this.cartItems
        .map((item) => String(item?.id || ''))
        .filter(Boolean),
    )];

    this.cartTypes = new Set(
      this.cartItems
        .map((item) => this.normalizeText(item?.type || ''))
        .filter(Boolean),
    );

    this.cartTitles = this.cartItems
      .map((item) => this.normalizeText(item?.title || ''))
      .filter(Boolean);

    this.cartAveragePrice = this.getCartAveragePrice();

    if (this.cartProductIds.length === 0) {
      this.hidden = true;
      return;
    }

    this.abortController = new AbortController();
    this.renderSkeleton();
    this.loadRecommendations();
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  clampNumber(value, min, max) {
    if (!Number.isFinite(value)) return min;
    return Math.min(Math.max(value, min), max);
  }

  readJSON(selector, fallback) {
    const script = this.querySelector(selector);
    if (!script) return fallback;

    try {
      return JSON.parse(script.textContent.trim() || JSON.stringify(fallback));
    } catch (error) {
      console.warn('Armonia cart upsell: JSON verisi okunamadı.', error);
      return fallback;
    }
  }

  get routesRoot() {
    return window.Shopify?.routes?.root || '/';
  }

  get remainingToFreeShipping() {
    if (this.freeShippingThreshold <= 0) return 0;
    return Math.max(this.freeShippingThreshold - this.cartTotal, 0);
  }

  getCartAveragePrice() {
    if (!Array.isArray(this.cartItems) || this.cartItems.length === 0) return 0;

    const prices = this.cartItems
      .map((item) => Number(item?.price || 0))
      .filter((price) => Number.isFinite(price) && price > 0);

    if (prices.length === 0) return 0;
    return prices.reduce((sum, price) => sum + price, 0) / prices.length;
  }

  async loadRecommendations() {
    try {
      const sourceProductIds = [...new Set(this.cartProductIds)].slice(0, 3);

      const complementaryGroups = await Promise.all(
        sourceProductIds.map((productId) => this.fetchRecommendations(productId, 'complementary')),
      );

      const complementary = this.collectCandidates(complementaryGroups.flat(), 'complementary');
      const fallback = this.collectCandidates(this.fallbackProducts, 'fallback');

      let related = [];

      if (this.relatedEnabled) {
        const relatedGroups = await Promise.all(
          sourceProductIds.map((productId) => this.fetchRecommendations(productId, 'related')),
        );

        related = this.collectCandidates(relatedGroups.flat(), 'related');
      }

      this.products = this.selectBestProducts([
        ...complementary,
        ...fallback,
        ...related,
      ]);

      if (this.products.length === 0) {
        this.removeAttribute('aria-busy');
        this.hidden = true;
        return;
      }

      this.render();
    } catch (error) {
      if (error.name === 'AbortError') return;

      const fallback = this.collectCandidates(this.fallbackProducts, 'fallback');
      this.products = this.selectBestProducts(fallback);

      if (this.products.length > 0) {
        this.render();
      } else {
        this.removeAttribute('aria-busy');
        this.hidden = true;
      }
    }
  }

  async fetchRecommendations(productId, intent) {
    const url = new URL(`${this.routesRoot}recommendations/products.json`, window.location.origin);
    url.searchParams.set('product_id', productId);
    url.searchParams.set('limit', '10');
    url.searchParams.set('intent', intent);

    try {
      const response = await fetch(url.toString(), {
        headers: {
          Accept: 'application/json',
        },
        signal: this.abortController?.signal,
      });

      if (!response.ok) return [];

      const data = await response.json();
      return Array.isArray(data.products) ? data.products : [];
    } catch (error) {
      if (error.name === 'AbortError') throw error;
      return [];
    }
  }

  collectCandidates(products, source) {
    if (!Array.isArray(products)) return [];

    const seen = new Set();

    return products.reduce((result, product) => {
      const id = String(product?.id || '');

      if (!id) return result;
      if (seen.has(id)) return result;
      if (this.cartProductIds.includes(id)) return result;
      if (!this.isUsableProduct(product)) return result;

      seen.add(id);
      result.push({ product, source });
      return result;
    }, []);
  }

  selectBestProducts(candidates) {
    const unique = new Map();

    candidates.forEach((candidate) => {
      const id = String(candidate?.product?.id || '');
      if (!id) return;

      const previous = unique.get(id);

      if (!previous || this.getSourcePriority(candidate.source) > this.getSourcePriority(previous.source)) {
        unique.set(id, candidate);
      }
    });

    const pool = [...unique.values()];
    const selected = [];
    const selectedTypes = new Set();

    while (selected.length < this.limit && pool.length > 0) {
      let bestIndex = -1;
      let bestScore = -Infinity;

      pool.forEach((candidate, index) => {
        const score = this.scoreCandidate(candidate, selectedTypes);

        if (score > bestScore) {
          bestScore = score;
          bestIndex = index;
        }
      });

      if (bestIndex < 0) break;

      const [winner] = pool.splice(bestIndex, 1);
      selected.push(winner.product);

      const type = this.getProductType(winner.product);
      if (type) selectedTypes.add(type);
    }

    return selected;
  }

  getSourcePriority(source) {
    if (source === 'complementary') return 3;
    if (source === 'fallback') return 2;
    return 1;
  }

  scoreCandidate(candidate, selectedTypes) {
    const { product, source } = candidate;

    let score = 0;

    if (source === 'complementary') score += 2400;
    if (source === 'fallback') score += 1500;
    if (source === 'related') score += 500;

    const productType = this.getProductType(product);
    const title = this.normalizeText(product?.title || '');
    const price = Number(product?.price || 0);

    if (productType) {
      if (this.cartTypes.has(productType)) {
        score -= source === 'related' ? 260 : 150;
      } else {
        score += 85;
      }

      if (selectedTypes.has(productType)) {
        score -= 80;
      } else {
        score += 40;
      }
    }

    if (title && this.isTitleTooSimilarToCart(title)) {
      score -= source === 'related' ? 220 : 110;
    }

    score += this.getPriceFitScore(price);

    if (source === 'fallback') {
      const index = this.fallbackProducts.findIndex(
        (fallbackProduct) => String(fallbackProduct?.id || '') === String(product?.id || ''),
      );

      if (index >= 0) {
        score += Math.max(80 - index * 3, 0);
      }
    }

    return score;
  }

  getPriceFitScore(price) {
    if (!Number.isFinite(price) || price <= 0) return -40;

    const remaining = this.remainingToFreeShipping;

    if (remaining > 0) {
      const ratio = price / remaining;

      if (ratio >= 0.72 && ratio <= 1.08) return 180;
      if (ratio >= 0.45 && ratio < 0.72) return 135;
      if (ratio > 1.08 && ratio <= 1.35) return 95;
      if (ratio >= 0.25 && ratio < 0.45) return 80;
      if (ratio < 0.25) return 25;

      return -30;
    }

    if (this.cartAveragePrice > 0) {
      if (price <= this.cartAveragePrice) return 80;
      if (price <= this.cartAveragePrice * 1.35) return 35;
      return -20;
    }

    return 0;
  }

  isTitleTooSimilarToCart(candidateTitle) {
    const candidateWords = this.getMeaningfulWords(candidateTitle);

    if (candidateWords.length === 0) return false;

    return this.cartTitles.some((cartTitle) => {
      const cartWords = new Set(this.getMeaningfulWords(cartTitle));
      const overlap = candidateWords.filter((word) => cartWords.has(word)).length;
      return overlap >= 2;
    });
  }

  getMeaningfulWords(value) {
    const stopWords = new Set([
      'za', 'sa', 'i', 'u', 'od', 'do', 'na', 'set', 'kom', 'cm', 'x',
      'boja', 'model', 'velicina', 'veličina',
    ]);

    return this.normalizeText(value)
      .split(/[^a-z0-9čćžšđ]+/i)
      .map((word) => word.trim())
      .filter((word) => word.length >= 3 && !stopWords.has(word));
  }

  normalizeText(value) {
    return String(value || '')
      .toLocaleLowerCase('sr-Latn-RS')
      .normalize('NFKC')
      .replace(/\s+/g, ' ')
      .trim();
  }

  getProductType(product) {
    return this.normalizeText(
      product?.type || product?.product_type || product?.productType || '',
    );
  }

  isUsableProduct(product) {
    if (!product || product.available === false) return false;

    const variant = this.getAvailableVariant(product);
    const price = Number(product.price || variant?.price || 0);

    return Boolean(product.url && variant?.id && price > 0);
  }

  getAvailableVariant(product) {
    if (Array.isArray(product.variants) && product.variants.length > 0) {
      return product.variants.find((variant) => variant.available !== false) || null;
    }

    if (product.variant_id) {
      return {
        id: product.variant_id,
        available: product.variant_available !== false,
      };
    }

    return null;
  }

  getVariantCount(product) {
    if (Array.isArray(product.variants)) return product.variants.length;

    const count = Number(product.variant_count || 0);
    return Number.isFinite(count) ? count : 0;
  }

  renderSkeleton() {
    this.hidden = false;
    this.setAttribute('aria-busy', 'true');

    const content = this.querySelector('[data-armonia-upsell-content]');
    if (!content) return;

    content.replaceChildren();

    const section = document.createElement('section');
    section.className = 'armonia-cart-upsell__panel is-loading';
    section.setAttribute('aria-hidden', 'true');

    const header = document.createElement('div');
    header.className = 'armonia-cart-upsell__header';

    const heading = document.createElement('div');
    heading.className = 'armonia-cart-upsell__skeleton-heading';

    header.appendChild(heading);
    section.appendChild(header);

    const list = document.createElement('div');
    list.className = 'armonia-cart-upsell__list';

    for (let index = 0; index < this.limit; index += 1) {
      const item = document.createElement('div');
      item.className = 'armonia-cart-upsell__skeleton-item';
      item.innerHTML = `
        <span class="armonia-cart-upsell__skeleton-image"></span>
        <span class="armonia-cart-upsell__skeleton-copy">
          <span></span>
          <span></span>
        </span>
        <span class="armonia-cart-upsell__skeleton-button"></span>
      `;
      list.appendChild(item);
    }

    section.appendChild(list);
    content.appendChild(section);
  }

  render() {
    const content = this.querySelector('[data-armonia-upsell-content]');
    if (!content) return;

    content.replaceChildren();

    const section = document.createElement('section');
    section.className = 'armonia-cart-upsell__panel';
    section.setAttribute('aria-labelledby', 'ArmoniaCartUpsellHeading');

    if (this.remainingToFreeShipping > 0) {
      section.classList.add('is-shipping-goal');
    }

    const header = document.createElement('div');
    header.className = 'armonia-cart-upsell__header';

    const heading = document.createElement('h3');
    heading.id = 'ArmoniaCartUpsellHeading';
    heading.className = 'armonia-cart-upsell__heading';
    heading.textContent =
      this.remainingToFreeShipping > 0
        ? this.labels.shippingHeading
        : this.labels.defaultHeading;

    header.appendChild(heading);
    section.appendChild(header);

    const list = document.createElement('div');
    list.className = 'armonia-cart-upsell__list';

    this.products.forEach((product) => {
      list.appendChild(this.createProductRow(product));
    });

    section.appendChild(list);

    const error = document.createElement('p');
    error.className = 'armonia-cart-upsell__error';
    error.hidden = true;
    error.setAttribute('role', 'alert');
    error.setAttribute('data-armonia-upsell-error', '');
    section.appendChild(error);

    content.appendChild(section);

    this.removeAttribute('aria-busy');
    this.hidden = false;
  }

  createProductRow(product) {
    const row = document.createElement('article');
    row.className = 'armonia-cart-upsell__item';

    const productUrl = this.normalizeProductUrl(product.url);
    const imageUrl = this.getImageUrl(product);

    const mediaLink = document.createElement('a');
    mediaLink.className = 'armonia-cart-upsell__media';
    mediaLink.href = productUrl;
    mediaLink.setAttribute('aria-label', product.title || '');

    if (imageUrl) {
      const image = document.createElement('img');
      image.src = imageUrl;
      image.alt = product.title || '';
      image.loading = 'lazy';
      image.width = 160;
      image.height = 160;
      mediaLink.appendChild(image);
    } else {
      const placeholder = document.createElement('span');
      placeholder.className = 'armonia-cart-upsell__image-placeholder';
      placeholder.setAttribute('aria-hidden', 'true');
      mediaLink.appendChild(placeholder);
    }

    const copy = document.createElement('div');
    copy.className = 'armonia-cart-upsell__copy';

    const title = document.createElement('a');
    title.className = 'armonia-cart-upsell__title';
    title.href = productUrl;
    title.textContent = product.title || '';

    const price = document.createElement('div');
    price.className = 'armonia-cart-upsell__price';

    const currentPrice = Number(product.price || 0);
    const compareAtPrice = Number(product.compare_at_price || 0);
    const priceVaries = product.price_varies === true;

    const current = document.createElement('span');
    current.className = 'armonia-cart-upsell__price-current';
    current.textContent = `${priceVaries ? `${this.labels.from} ` : ''}${this.formatMoney(currentPrice)}`;
    price.appendChild(current);

    if (!priceVaries && compareAtPrice > currentPrice) {
      const compare = document.createElement('s');
      compare.className = 'armonia-cart-upsell__price-compare';
      compare.textContent = this.formatMoney(compareAtPrice);
      price.appendChild(compare);
    }

    copy.appendChild(title);
    copy.appendChild(price);

    const variant = this.getAvailableVariant(product);
    const isSingleVariant = this.getVariantCount(product) === 1;

    let action;

    if (isSingleVariant && variant?.id) {
      action = document.createElement('button');
      action.type = 'button';
      action.className = 'armonia-cart-upsell__action';
      action.textContent = this.labels.add;
      action.dataset.variantId = String(variant.id);

      action.addEventListener('click', () => {
        this.addToCart(variant.id, action);
      });
    } else {
      action = document.createElement('a');
      action.className = 'armonia-cart-upsell__action armonia-cart-upsell__action--secondary';
      action.href = productUrl;
      action.textContent = this.labels.choose;
    }

    row.appendChild(mediaLink);
    row.appendChild(copy);
    row.appendChild(action);

    return row;
  }

  normalizeProductUrl(url) {
    const value = String(url || '');

    if (!value) return this.routesRoot;
    if (/^https?:\/\//i.test(value)) return value;
    if (value.startsWith(this.routesRoot)) return value;

    if (value.startsWith('/')) {
      if (this.routesRoot === '/') return value;
      return `${this.routesRoot.replace(/\/$/, '')}${value}`;
    }

    return `${this.routesRoot}${value}`;
  }

  getImageUrl(product) {
    let source = product.featured_image;

    if (!source && Array.isArray(product.images) && product.images.length > 0) {
      source = product.images[0];
    }

    if (!source) return '';

    let value = String(source);

    if (value.startsWith('//')) {
      value = `${window.location.protocol}${value}`;
    }

    try {
      const url = new URL(value, window.location.origin);

      if (url.hostname.includes('cdn.shopify.com')) {
        url.searchParams.set('width', '180');
      }

      return url.toString();
    } catch (error) {
      return value;
    }
  }

  formatMoney(cents) {
    const value = Number(cents || 0) / 100;
    const hasDecimals = Math.round(Number(cents || 0)) % 100 !== 0;

    const formatted = new Intl.NumberFormat('sr-Latn-RS', {
      minimumFractionDigits: hasDecimals ? 2 : 0,
      maximumFractionDigits: hasDecimals ? 2 : 0,
    }).format(value);

    return `${formatted} RSD`;
  }

  async addToCart(variantId, button) {
    if (!variantId || button.disabled) return;

    this.hideAddError();

    const originalLabel = button.textContent;
    button.disabled = true;
    button.classList.add('is-loading');
    button.textContent = this.labels.adding;

    try {
      const response = await fetch(`${this.routesRoot}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [
            {
              id: Number(variantId),
              quantity: 1,
            },
          ],
          sections: ['cart-drawer', 'cart-icon-bubble'],
          sections_url: window.location.pathname,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.description || 'Cart add failed');
      }

      const cartDrawer = document.querySelector('cart-drawer');

      if (cartDrawer && typeof cartDrawer.renderContents === 'function' && data.sections) {
        data.id = Number(variantId);
        cartDrawer.renderContents(data);
        return;
      }

      window.location.reload();
    } catch (error) {
      button.disabled = false;
      button.classList.remove('is-loading');
      button.textContent = originalLabel;
      this.showAddError();
    }
  }

  showAddError() {
    const error = this.querySelector('[data-armonia-upsell-error]');
    if (!error) return;

    error.textContent = this.labels.addError;
    error.hidden = false;
  }

  hideAddError() {
    const error = this.querySelector('[data-armonia-upsell-error]');
    if (!error) return;

    error.hidden = true;
    error.textContent = '';
  }
}

if (!customElements.get('armonia-cart-upsell')) {
  customElements.define('armonia-cart-upsell', ArmoniaCartUpsell);
}
